import React, { useEffect, useMemo, useState } from 'react';
import { addYears, format, isAfter, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Skeleton } from 'primereact/skeleton';
import { PrimeIcons } from 'primereact/api';
import classNames from 'classnames';
import useSWR from 'swr';

import { useAuthContext } from '../../context/AuthContext';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { usePopups } from '../../context/PopupContext';
import { useReservationTypes } from '../../hooks/AppData/useReservationTypes';
import { useClient } from '../../hooks/useClient';
import { useForm } from '../../hooks/useForm';
import { showDeleteDialog } from '../../utils/dialogUtils';
import { hasFeaturesPermission } from '../../utils/featureUtils';
import { capitalize } from '../../utils/textUtils';
import { CANCELED_BY_OPTIONS, CancellationReason, Reservation, RESERVATION_STATUSES } from '../../types/reservation';
import { FormField } from '../FormField/FormField';
import { CancellationForm, getCancellationFormValidators, getInitialCancellationForm } from '../ReservationPanel/validators';
import './BulkCancelReservationsDialog.scss';

interface BulkCancelResult {
  cancelledReservationIds: string[];
  removedFromQueueReservationIds: string[];
  failed: { reservationId: string; message: string }[];
}

// The backend only sends a technical `message` for each reservation it could not cancel, so the toast never shows it
const BULK_CANCEL_FAILED_DETAIL = 'No se han podido cancelar las reservas';

interface BulkCancelReservationsDialogProps {
  visible: boolean;
  onHide: () => void;
  externalUserId: string;
  userName: string;
  onCancelled: () => void;
}

const canceledByOptions = [
  { label: 'Empleado', value: CANCELED_BY_OPTIONS.CANCELED_BY_ADMIN },
  { label: 'Cliente', value: CANCELED_BY_OPTIONS.CANCELED_BY_USER },
];

const cancellationReasonOptions = [
  { label: 'Baja de usuario', value: CancellationReason.USER_DISABLED },
  { label: 'No se presentó', value: CancellationReason.USER_DID_NOT_SHOW_UP },
  { label: 'Conflicto de horarios', value: CancellationReason.SCHEDULE_CONFLICT },
  { label: 'Problema de salud', value: CancellationReason.HEALTH_ISSUE },
  { label: 'Motivo personal', value: CancellationReason.PERSONAL_REASON },
  { label: 'Ya no necesita el servicio', value: CancellationReason.NO_LONGER_NEEDED },
  { label: 'Problema de transporte', value: CancellationReason.TRANSPORT_ISSUE },
  { label: 'Otro', value: CancellationReason.OTHER },
];

const buildCancellationPayload = (cancellationForm: CancellationForm) => {
  const canceledByAdmin = cancellationForm.canceledBy === CANCELED_BY_OPTIONS.CANCELED_BY_ADMIN;
  return {
    canceledBy: canceledByAdmin ? CANCELED_BY_OPTIONS.CANCELED_BY_ADMIN : cancellationForm.canceledBy!,
    cancellationReason: canceledByAdmin ? CancellationReason.OTHER : cancellationForm.cancellationReason!,
    ...(cancellationForm.cancellationReason === CancellationReason.OTHER || canceledByAdmin
      ? { customCancellationReason: cancellationForm.customCancellationReason }
      : {}),
  };
};

const getOutcomeLabel = (
  reservation: Reservation,
  reservationType: { groupSize: number; hasUnregisteredClients?: boolean } | undefined,
) => {
  if (reservation.externalUserStatus === RESERVATION_STATUSES.IN_QUEUE) {
    return 'En lista de espera. Se quita a este cliente y la reserva sigue.';
  }

  const confirmedCount = reservation.confirmedExternalUsers?.length ?? reservation.externalUsers?.length;
  if ((confirmedCount ?? 0) > 1) {
    return 'Reserva de grupo. Solo se cancelará a este cliente.';
  }
  if (confirmedCount === 1 || reservationType?.groupSize === 1) {
    return 'Se cancelará la reserva.';
  }
  return 'Se cancelará la reserva.';
};

export const BulkCancelReservationsDialog = ({
  visible,
  onHide,
  externalUserId,
  userName,
  onCancelled,
}: BulkCancelReservationsDialogProps) => {
  const { get, post } = useClient();
  const { showToast } = usePopups();
  const { organization } = useOrganizationContext();
  const { features, featuresLoading } = useAuthContext();
  const { reservationTypeFromId, reservationTypesLoading } = useReservationTypes();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const upcomingUrl = useMemo(() => {
    if (!organization?.id || !externalUserId) return null;
    const startDate = new Date().toISOString();
    const endDate = addYears(new Date(), 2).toISOString();
    return `/organizations/${organization.id}/reservations?externalUserId=${externalUserId}&startDate=${startDate}&endDate=${endDate}`;
  }, [organization?.id, externalUserId]);

  const { data: reservations, isLoading } = useSWR(upcomingUrl, get<Reservation[]>);
  const listLoading = isLoading || !!featuresLoading || !!reservationTypesLoading;

  const cancellable = useMemo(() => {
    return (reservations ?? [])
      .filter((reservation) => {
        const status = reservation.externalUserStatus;
        if (status !== RESERVATION_STATUSES.CONFIRMED && status !== RESERVATION_STATUSES.IN_QUEUE) return false;
        if (!isAfter(parseISO(reservation.localStartDate), new Date())) return false;
        const reservationType = reservationTypeFromId(reservation.reservationTypeId);
        if (!reservationType) return false;
        return hasFeaturesPermission([reservationType.features.manage], { features, featuresLoading });
      })
      .sort((a, b) => parseISO(a.localStartDate).getTime() - parseISO(b.localStartDate).getTime());
  }, [reservations, reservationTypeFromId, features, featuresLoading]);

  useEffect(() => {
    if (!visible) {
      setSelectedIds([]);
    }
  }, [visible]);

  const initialCancellationForm = useMemo(() => getInitialCancellationForm(false), []);
  const cancellationFormValidators = useMemo(() => getCancellationFormValidators(false), []);
  const {
    form: cancellationForm,
    setFormFields: setCancellationFormFields,
    isSaving,
    setIsSaving,
    validationErrors: cancellationErrors,
    validate: validateCancellationForm,
    resetForm,
  } = useForm<CancellationForm>(initialCancellationForm, true, cancellationFormValidators);

  const allSelected = cancellable.length > 0 && selectedIds.length === cancellable.length;
  const formDisabled = isSaving || listLoading || cancellable.length === 0;

  const toggleReservation = (reservationId: string) => {
    if (isSaving) return;
    setSelectedIds((current) =>
      current.includes(reservationId) ? current.filter((id) => id !== reservationId) : [...current, reservationId],
    );
  };

  const handleHide = () => {
    if (isSaving) return;
    resetForm();
    onHide();
  };

  const cancelReservations = async (reservationIds: string[], cancellation: CancellationForm) => {
    if (isSaving || !organization?.id || !externalUserId) return;
    setIsSaving(true);
    let result: BulkCancelResult | undefined;
    try {
      result = await post<BulkCancelResult>(
        `/organizations/${organization.id}/external-users/${externalUserId}/reservations/bulk-cancel`,
        {
          body: {
            reservationIds,
            cancellation: buildCancellationPayload(cancellation),
          },
          errorMessages: {
            summary: 'Error al cancelar las reservas',
            defaultDetail: 'No se han podido cancelar las reservas',
          },
        },
      );
    } catch {
      showToast?.({
        severity: 'error',
        summary: 'Error al cancelar las reservas',
        detail: 'No se han podido cancelar las reservas',
      });
      return;
    } finally {
      setIsSaving(false);
    }
    if (!result) return;

    const succeeded = result.cancelledReservationIds.length + result.removedFromQueueReservationIds.length;
    if (result.failed.length && succeeded === 0) {
      showToast?.({
        severity: 'error',
        summary: 'No se han podido cancelar',
        detail: BULK_CANCEL_FAILED_DETAIL,
      });
      return;
    }

    if (result.failed.length) {
      showToast?.({
        severity: 'warn',
        summary: 'Cancelación parcial',
        detail: `Se han procesado ${succeeded} reservas. ${result.failed.length} no se han podido cancelar.`,
      });
    } else {
      showToast?.({
        severity: 'success',
        summary: 'Reservas canceladas',
        detail:
          succeeded === 1
            ? 'Se ha cancelado 1 reserva de este cliente.'
            : `Se han cancelado ${succeeded} reservas de este cliente.`,
      });
    }

    onCancelled();
    handleHide();
  };

  const handleConfirm = () => {
    if (isSaving || listLoading || !selectedIds.length || !organization?.id || !externalUserId) return;
    const errors = validateCancellationForm();
    if (errors) return;

    const reservationIds = [...selectedIds];
    const cancellation = { ...cancellationForm };
    const count = reservationIds.length;
    showDeleteDialog(
      () => {
        void cancelReservations(reservationIds, cancellation);
      },
      {
        header: 'Confirmar cancelación',
        message: `Vas a cancelar ${count} ${count === 1 ? 'reserva' : 'reservas'} de ${userName || 'este cliente'}. Esta acción no se puede deshacer. ¿Quieres continuar?`,
        acceptLabel: `Sí, cancelar ${count} ${count === 1 ? 'reserva' : 'reservas'}`,
      },
    );
  };

  return (
    <Dialog
      draggable={false}
      resizable={false}
      modal
      dismissableMask={!isSaving}
      closable={!isSaving}
      closeOnEscape={!isSaving}
      visible={visible}
      onHide={handleHide}
      header={
        <div className='bulk-cancel-heading'>
          <span>Cancelar reservas</span>
          {userName && <span className='bulk-cancel-client'>{userName}</span>}
        </div>
      }
      className='bulk-cancel-dialog big-modal'
      footer={
        <div className='dialog-footer'>
          <Button label='Volver' icon='pi pi-times' onClick={handleHide} className='p-button-text' disabled={isSaving} />
          <Button
            label={
              selectedIds.length
                ? `Cancelar ${selectedIds.length} ${selectedIds.length === 1 ? 'reserva' : 'reservas'}`
                : 'Cancelar reservas'
            }
            icon='pi pi-check'
            onClick={handleConfirm}
            className='p-button-danger'
            disabled={isSaving || !selectedIds.length || listLoading || !organization?.id || !externalUserId}
            loading={isSaving}
          />
        </div>
      }
    >
      <div className='bulk-cancel-layout'>
        <div className='cancellation-info warning'>
          <i className={classNames(PrimeIcons.EXCLAMATION_TRIANGLE, 'warning-icon')} aria-hidden='true' />
          <div>
            <p className='warning-title'>Solo se cancela a este cliente.</p>
            <p>En sesiones de grupo, los demás mantienen su reserva.</p>
          </div>
        </div>

        <form className='cancellation-form'>
          <FormField elementId='bulk-cancel-author' labelTitle='¿Quién cancela?' error={cancellationErrors?.canceledBy} required>
            <Dropdown
              inputId='bulk-cancel-author'
              value={cancellationForm.canceledBy}
              options={canceledByOptions}
              onChange={(e) => setCancellationFormFields({ canceledBy: e.value })}
              placeholder='Selecciona quién cancela'
              invalid={!!cancellationErrors?.canceledBy}
              disabled={formDisabled}
            />
          </FormField>

          <FormField
            elementId='bulk-cancel-reason'
            labelTitle='Motivo de cancelación'
            error={cancellationErrors?.cancellationReason}
            required={cancellationForm.canceledBy !== CANCELED_BY_OPTIONS.CANCELED_BY_ADMIN}
          >
            {cancellationForm.canceledBy === CANCELED_BY_OPTIONS.CANCELED_BY_ADMIN ? (
              <InputTextarea
                id='bulk-cancel-reason'
                value={cancellationForm.customCancellationReason}
                onChange={(e) => setCancellationFormFields({ customCancellationReason: e.target.value })}
                rows={1}
                autoResize
                placeholder='Motivo de cancelación'
                disabled={formDisabled}
              />
            ) : (
              <Dropdown
                inputId='bulk-cancel-reason'
                value={cancellationForm.cancellationReason}
                options={cancellationReasonOptions}
                onChange={(e) => setCancellationFormFields({ cancellationReason: e.value })}
                placeholder='Selecciona el motivo'
                invalid={!!cancellationErrors?.cancellationReason}
                disabled={formDisabled}
              />
            )}
          </FormField>

          {cancellationForm.cancellationReason === CancellationReason.OTHER &&
            cancellationForm.canceledBy === CANCELED_BY_OPTIONS.CANCELED_BY_USER && (
              <FormField
                elementId='bulk-cancel-custom-reason'
                className='bulk-cancel-custom-reason'
                labelTitle='Especifica el motivo'
                error={cancellationErrors?.customCancellationReason}
                required
              >
                <InputTextarea
                  id='bulk-cancel-custom-reason'
                  value={cancellationForm.customCancellationReason}
                  onChange={(e) => setCancellationFormFields({ customCancellationReason: e.target.value })}
                  rows={2}
                  invalid={!!cancellationErrors?.customCancellationReason}
                  disabled={formDisabled}
                />
              </FormField>
            )}
        </form>

        <section className='bulk-cancel-list' aria-label='Reservas futuras' aria-busy={listLoading}>
          <div className='bulk-cancel-list-header'>
            {cancellable.length > 0 && !listLoading ? (
              <label className='bulk-cancel-select-all'>
                <Checkbox
                  checked={allSelected}
                  disabled={isSaving}
                  onChange={() => setSelectedIds(allSelected ? [] : cancellable.map((reservation) => reservation.id))}
                />
                <span>Seleccionar todas</span>
              </label>
            ) : (
              <span className='bulk-cancel-list-title'>Reservas futuras</span>
            )}
            {!listLoading && cancellable.length > 0 && (
              <span className='bulk-cancel-count'>
                {selectedIds.length} / {cancellable.length}
              </span>
            )}
          </div>

          {listLoading && (
            <div className='bulk-cancel-skeletons'>
              {Array.from({ length: 4 }, (_, index) => (
                <div key={index} className='bulk-cancel-skeleton-row'>
                  <Skeleton shape='circle' size='1.25rem' />
                  <div>
                    <Skeleton width='70%' height='1rem' />
                    <Skeleton width='48%' height='0.75rem' />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!listLoading && cancellable.length === 0 && (
            <div className='bulk-cancel-empty'>
              <i className={PrimeIcons.CALENDAR} aria-hidden='true' />
              <p>Este cliente no tiene reservas futuras.</p>
            </div>
          )}
          {!listLoading && cancellable.length > 0 && (
            <ul>
              {cancellable.map((reservation) => {
                const reservationType = reservationTypeFromId(reservation.reservationTypeId);
                const checked = selectedIds.includes(reservation.id);
                return (
                  <li key={reservation.id} className={classNames({ selected: checked })}>
                    <label>
                      <Checkbox checked={checked} disabled={isSaving} onChange={() => toggleReservation(reservation.id)} />
                      <span className='bulk-cancel-date' aria-hidden='true'>
                        <span>{format(parseISO(reservation.localStartDate), 'MMM', { locale: es })}</span>
                        <strong>{format(parseISO(reservation.localStartDate), 'd')}</strong>
                      </span>
                      <span className='bulk-cancel-reservation'>
                        <strong>{reservationType?.name ? capitalize(reservationType.name) : 'Reserva'}</strong>
                        <span className='bulk-cancel-meta'>
                          <span>{format(parseISO(reservation.localStartDate), 'EEE d MMM yyyy · HH:mm', { locale: es })}</span>
                          {reservation.branch?.name && (
                            <span>
                              <i className={PrimeIcons.MAP_MARKER} aria-hidden='true' /> {reservation.branch.name}
                            </span>
                          )}
                        </span>
                        <small className='bulk-cancel-outcome'>{getOutcomeLabel(reservation, reservationType)}</small>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </Dialog>
  );
};
